import './wdyr';
import { configureAuth } from '@artic/api-client';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { LicenseManager } from 'ag-grid-enterprise';
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { IntercomProvider } from 'react-use-intercom';

import { redirectToHub } from './auth';

import './index.css';
import { queryClient, router } from './router';

console.info(`[quality] v${__APP_VERSION__} (${__COMMIT_SHA__}) built ${__BUILD_TIME__}`);

const sentryDsn = import.meta.env['VITE_APP_SENTRY_DSN'] as string | undefined;
const sentryEnvironment = import.meta.env['VITE_APP_SENTRY_ENVIRONMENT'] as string | undefined;

Sentry.init({
  ...(sentryDsn ? { dsn: sentryDsn } : {}),
  ...(sentryEnvironment ? { environment: sentryEnvironment } : {}),
  release: `quality@${__APP_VERSION__}`,
  tracesSampleRate: 0.01,
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration({
      enableInp: true,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.browserProfilingIntegration(),
  ],
  beforeSend(event, hint) {
    const error = hint?.originalException;
    if (error instanceof DOMException && error.name === 'AbortError') {
      return null;
    }
    if (error instanceof Error && /signal is aborted/i.test(error.message)) {
      return null;
    }
    return event;
  },
});

const agGridLicense = (import.meta.env['VITE_AG_GRID_LICENSE'] as string | undefined) ?? '';
if (agGridLicense) {
  LicenseManager.setLicenseKey(agGridLicense);
}

configureAuth({
  cookieDomain: (import.meta.env['VITE_AUTH_COOKIE_DOMAIN'] as string | undefined) ?? undefined,
  onRefreshFailure: () => redirectToHub(),
});

const intercomAppId = import.meta.env['VITE_APP_INTERCOM_APP_ID'] as string | undefined;

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root element');

createRoot(root).render(
  <StrictMode>
    <IntercomProvider appId={intercomAppId ?? ''} autoBoot={Boolean(intercomAppId)}>
      <QueryClientProvider client={queryClient}>
        <NuqsAdapter>
          <RouterProvider router={router} />
        </NuqsAdapter>
      </QueryClientProvider>
    </IntercomProvider>
  </StrictMode>,
);
